import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserManagementService } from '../../../core/services/user-management.service';
import { HttpEventType } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UtilitiesService } from '@nx-ahs/shared/services';
import { ConfirmationModalService, ModalTemplateComponent, ProgressButtonComponent } from '@nx-ahs/shared/components';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
	selector: 'app-upload-users',
	templateUrl: './upload-users.component.html',
	styleUrls: ['./upload-users.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatButtonModule,
		MatFormFieldModule,
		MatRadioModule,
		FontAwesomeModule,
		ModalTemplateComponent,
		ProgressButtonComponent
	]
})
export class UploadUsersComponent implements OnInit {
	public azure_string: string = 'Azure AD';
	public local_string: string = 'Local';

	@ViewChild('fileUpload') fileUpload: ElementRef;
	public form: FormGroup;
	public downloading: boolean;
	public uploading: boolean;
	public userType: string;
	public title: string = 'Upload Users';

	constructor(
		private userService: UserManagementService,
		private formBuilder: FormBuilder,
		private utilitiesService: UtilitiesService,
		private snackBar: MatSnackBar,
		private dialogRef: MatDialogRef<UploadUsersComponent>,
		private confirmationModalService: ConfirmationModalService
	) {}

	ngOnInit() {
		this.buildForm();

		this.dialogRef.keydownEvents().subscribe((event) => {
			if (event.key === 'Escape') {
				this.cancel();
			}
		});
		this.dialogRef.backdropClick().subscribe(() => {
			this.cancel();
		});
	}

	private buildForm(): void {
		this.form = this.formBuilder.group({
			userType: ['', Validators.required],
			file: ['', Validators.required]
		});
	}

	public clearFile(): void {
		if (!this.uploading) {
			this.form.get('file').setValue(null);
			this.fileUpload.nativeElement.value = null;
		}
	}

	public downloadTemplate(): void {
		// If the user type has been set then download the template for that type, else display validation error
		if (this.form.get('userType').value) {
			this.downloading = true;
			this.userService
				.getUserUploadTemplate(this.form.get('userType').value == this.azure_string ? true : false)
				.finally(() => {
					this.downloading = false;
				});
		} else {
			this.form.get('userType').markAsTouched();
		}
	}

	public fileSelected(): void {
		this.form.get('file').setValue(this.fileUpload.nativeElement.files[0]);
		this.fileUpload.nativeElement.value = null;
	}

	public uploadTemplate(): void {
		// Mark all fields on the form as touched so validations appear
		this.utilitiesService.markAllFieldsTouched(this.form);

		// If the form is valid then proceed with upload
		if (this.form.valid) {
			// Create form data to post to server and populate with the selected file
			const file = this.form.get('file').value;
			const formData = new FormData();
			formData.append('file', file, file.name);

			// Update UI
			this.uploading = true;
			this.form.disable();

			// Upload file
			this.userService
				.uploadUsers(formData, this.form.get('userType').value == this.azure_string ? true : false)
				.subscribe(
					(event) => {
						if (event && event.type == HttpEventType.UploadProgress) {
							// Calculate progress percent
							const progress = Math.round((event.loaded * 100) / event.total);

							// If the file upload is complete close the sidebar and let user know to check notification window
							if (progress == 100) {
								this.snackBar.open(
									'Your file has been uploaded to the server.  Check your notifications for updates on the processing status.',
									'DISMISS',
									{ duration: 5000 }
								);
								this.dialogRef.close();
							}
						}
					},
					null,
					null
				);
		}
	}

	public cancel(): void {
		this.confirmationModalService
			.showConfirmationDialog('Confirm Cancel', 'Click OK to continue')
			.beforeClosed()
			.toPromise()
			.then((result) => {
				if (result) {
					this.dialogRef.close(undefined);
				}
			});
	}
}
